import { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/contexts/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  ArrowLeft,
  GraduationCap,
  Users,
  Star,
  Briefcase,
  LogIn,
  Mail,
  Database,
  Trash2,
  Eraser,
  Download,
  Plus,
  ExternalLink,
  Clock,
  Save,
  Server,
  CheckCircle2,
  RefreshCw,
  Image,
  School,
  Settings2,
  BarChart3,
  BookOpen,
  Layers,
  HardDrive,
  Receipt,
  Construction,
  Rocket,
  Activity,
  Building2,
} from 'lucide-react';
import { V2DeploymentStatus } from '@/components/schools/V2DeploymentStatus';
import { V1DeployDialog } from '@/components/schools/V1DeployDialog';
import { V1DeploymentStatus } from '@/components/schools/V1DeploymentStatus';
import {
  GeneralInfoCard,
  EstablishmentsCard,
  ContactsCard,
  V1DeploymentCard,
  V2SubscriptionCard,
  BrandingImagesCard,
  V1DatabaseCard,
  V2DirectorsCard,
  DataImportsCard,
} from '@/components/schools/detail';
import { NetworkCard } from '@/components/schools/detail/NetworkCard';
import {
  getSchoolForEdit,
  getSchoolOptions,
  getSchoolMetrics,
  getSchoolMetricsHistory,
  getSchoolBackups,
  createSchoolBackup,
  deleteSchoolBackup,
  getBackupDownloadUrl,
} from '@/api/schools';
import { getV2Progress, resetV2Deployment, wipeV2Content, migrateV2, changeV2Status } from '@/api/v2-deployment';
import { V2ActivityTab } from './activity/V2ActivityTab';
import { getV2TenantGroups } from '@/api/white-labels';
import type { SchoolEditData, SchoolOptions } from '@/types/school';
import type { SchoolMetrics, MetricsHistory, Backup } from '@/api/schools';
import type { V2DeploymentInfo, V2TenantGroup } from '@/types/v2-deployment';
import { cn } from '@/lib/utils';
import { useBackupDownload } from '@/hooks/useBackupDownload';
import { DownloadProgressModal } from '@/components/backup/DownloadProgressModal';

type Env = 'v1' | 'v2';

interface StatCardProps {
  title: string;
  value: number | string;
  icon: React.ComponentType<{ className?: string }>;
  color: 'teal' | 'orange' | 'yellow' | 'blue' | 'red' | 'cyan' | 'pink' | 'green';
}

function StatCard({ title, value, icon: Icon, color }: StatCardProps) {
  const iconBgClasses = {
    teal: 'bg-teal-100 text-teal-600 dark:bg-teal-900/30 dark:text-teal-400',
    orange: 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400',
    yellow: 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400',
    blue: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
    red: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400',
    cyan: 'bg-cyan-100 text-cyan-600 dark:bg-cyan-900/30 dark:text-cyan-400',
    pink: 'bg-pink-100 text-pink-600 dark:bg-pink-900/30 dark:text-pink-400',
    green: 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
  };

  return (
    <div className="rounded-xl border shadow-sm bg-white dark:bg-slate-800/50 border-gray-100 dark:border-slate-700">
      <div className="p-4 flex items-center gap-3">
        <div className={cn('p-2.5 rounded-lg', iconBgClasses[color])}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{value}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">{title}</p>
        </div>
      </div>
    </div>
  );
}

type EnvLight = 'green' | 'red' | null;

interface EnvSwitchProps {
  env: Env;
  onChange: (env: Env) => void;
  /** Libellé complet du statut, affiché au survol. */
  v1Status: string;
  v2Status: string;
  /** Voyant : vert = opérationnel, rouge = problème, null = non déployé. */
  v1Light: EnvLight;
  v2Light: EnvLight;
}

/** Switch V1/V2 compact : filtre les éléments spécifiques à un environnement. */
function EnvSwitch({ env, onChange, v1Status, v2Status, v1Light, v2Light }: EnvSwitchProps) {
  const renderButton = (target: Env, status: string, light: EnvLight) => (
    <button
      type="button"
      onClick={() => onChange(target)}
      title={`${target.toUpperCase()} — ${status}`}
      className={cn(
        'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-semibold transition-colors',
        env === target
          ? target === 'v1'
            ? 'bg-blue-600 text-white shadow-sm'
            : 'bg-green-600 text-white shadow-sm'
          : 'text-muted-foreground hover:text-foreground'
      )}
    >
      {target.toUpperCase()}
      {light && (
        <span
          className={cn(
            'h-2 w-2 rounded-full',
            light === 'green' ? 'bg-green-400' : 'bg-red-500',
            env === target && light === 'green' && 'bg-white'
          )}
        />
      )}
    </button>
  );

  return (
    <div className="inline-flex items-center rounded-lg border bg-muted/50 p-0.5 gap-0.5 shrink-0">
      {renderButton('v1', v1Status, v1Light)}
      {renderButton('v2', v2Status, v2Light)}
    </div>
  );
}

function formatDateTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function formatHistoryDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
}

export function SchoolDetailPage() {
  const { code } = useParams<{ code: string }>();
  const { isFavorite, toggleFavorite, hasPermission } = useAuth();
  const [school, setSchool] = useState<SchoolEditData | null>(null);
  const [options, setOptions] = useState<SchoolOptions | null>(null);
  const [metrics, setMetrics] = useState<SchoolMetrics | null>(null);
  const [metricsHistory, setMetricsHistory] = useState<MetricsHistory[]>([]);
  const [metricsHistoryLoading, setMetricsHistoryLoading] = useState(false);
  const [backups, setBackups] = useState<Backup[]>([]);
  const [loading, setLoading] = useState(true);
  const [backupsLoading, setBackupsLoading] = useState(false);
  const { downloadInfo, modalOpen, setModalOpen, download: startDownload, reset: resetDownload } = useBackupDownload();
  const [error, setError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Compatibilité anciens liens : ?tab=v1|v2 devient env + onglet "environnement".
  const initialTabParam = searchParams.get('tab');
  const legacyEnv: Env | null =
    initialTabParam === 'v1'
      ? 'v1'
      : initialTabParam === 'v2' || initialTabParam === 'activite'
        ? 'v2'
        : null;
  const [env, setEnv] = useState<Env>(
    (searchParams.get('env') as Env) || legacyEnv || 'v1'
  );
  const [envFromUrl] = useState(Boolean(searchParams.get('env') || legacyEnv));
  const [activeTab, setActiveTab] = useState(
    initialTabParam === 'v1' || initialTabParam === 'v2'
      ? 'environnement'
      : initialTabParam || 'informations'
  );

  // L'onglet actif et l'environnement sont reflétés dans l'URL (?env=...&tab=...)
  // pour permettre le partage de liens directs (ex. journal d'activité V2 filtré).
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (tab === 'informations') {
          next.delete('tab');
        } else {
          next.set('tab', tab);
        }
        return next;
      },
      { replace: true }
    );
  };

  const handleEnvChange = (nextEnv: Env) => {
    if (nextEnv === env) return;
    // L'onglet Activité n'existe qu'en V2 : repli sur l'onglet Environnement.
    // L'onglet Branding n'existe qu'en V1 (le branding V2 se gère au réseau) :
    // repli sur l'onglet École.
    let nextTab = activeTab;
    if (nextEnv === 'v1' && activeTab === 'activite') nextTab = 'environnement';
    if (nextEnv === 'v2' && activeTab === 'branding') nextTab = 'informations';
    setEnv(nextEnv);
    setActiveTab(nextTab);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('env', nextEnv);
        if (nextTab === 'informations') {
          next.delete('tab');
        } else {
          next.set('tab', nextTab);
        }
        return next;
      },
      { replace: true }
    );
  };

  const [v1DeployDialogOpen, setV1DeployDialogOpen] = useState(false);
  const [v2Info, setV2Info] = useState<V2DeploymentInfo | null>(null);
  const [v2Loading, setV2Loading] = useState(false);
  const [v2ProgressRequested, setV2ProgressRequested] = useState(false);
  const [v2Resetting, setV2Resetting] = useState(false);
  const [v2Wiping, setV2Wiping] = useState(false);
  const [v2Suspending, setV2Suspending] = useState(false);
  const [v2MigrateDialogOpen, setV2MigrateDialogOpen] = useState(false);
  const [v2MigrateLoading, setV2MigrateLoading] = useState(false);
  const [v2TenantGroups, setV2TenantGroups] = useState<V2TenantGroup[]>([]);
  const [v2SelectedGroupId, setV2SelectedGroupId] = useState<string>('');

  useEffect(() => {
    if (code) {
      fetchData();
    }
  }, [code]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [schoolData, optionsData, metricsData] = await Promise.all([
        getSchoolForEdit(code!),
        getSchoolOptions(),
        getSchoolMetrics(code!),
      ]);
      setSchool(schoolData);
      setOptions(optionsData);
      setMetrics(metricsData);
      // Sans environnement explicite dans l'URL, on ouvre celui où l'école vit.
      if (!envFromUrl && !schoolData.hasV1 && schoolData.hasV2) {
        setEnv('v2');
      }
    } catch (err) {
      setError("Erreur lors du chargement de l'école");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const refreshSchool = useCallback(async () => {
    if (!code) return;
    try {
      const [schoolData, metricsData] = await Promise.all([
        getSchoolForEdit(code),
        getSchoolMetrics(code),
      ]);
      setSchool(schoolData);
      setMetrics(metricsData);
    } catch (err) {
      console.error('Error refreshing school:', err);
    }
  }, [code]);

  const refreshOptions = useCallback(async () => {
    try {
      const data = await getSchoolOptions();
      setOptions(data);
    } catch (err) {
      console.error('Failed to refresh options:', err);
    }
  }, []);

  const fetchBackups = async () => {
    try {
      setBackupsLoading(true);
      const data = await getSchoolBackups(code!);
      setBackups(data);
    } catch (err) {
      console.error('Error fetching backups:', err);
    } finally {
      setBackupsLoading(false);
    }
  };

  // Load backups when switching to the tab
  useEffect(() => {
    if (activeTab === 'backups' && backups.length === 0 && !backupsLoading) {
      fetchBackups();
    }
  }, [activeTab]);

  // Load metrics history when switching to statistiques tab
  useEffect(() => {
    if (activeTab === 'statistiques' && code && metricsHistory.length === 0 && !metricsHistoryLoading) {
      fetchMetricsHistory();
    }
  }, [activeTab]);

  const fetchMetricsHistory = async () => {
    if (!code) return;
    try {
      setMetricsHistoryLoading(true);
      const history = await getSchoolMetricsHistory(code);
      setMetricsHistory(history);
    } catch (err) {
      console.error('Erreur lors du chargement de l\'historique des métriques', err);
    } finally {
      setMetricsHistoryLoading(false);
    }
  };

  const fetchV2Info = async () => {
    if (!code) return;
    try {
      setV2Loading(true);
      const info = await getV2Progress(code);
      setV2Info(info);
    } catch {
      setV2Info(null);
    } finally {
      setV2Loading(false);
    }
  };

  const handleV2Reset = async () => {
    if (!code) return;
    if (
      !confirm(
        'Êtes-vous sûr de vouloir réinitialiser le déploiement V2 ? Cela supprimera le tenant V2 et effacera toutes les données associées.'
      )
    ) {
      return;
    }

    try {
      setV2Resetting(true);
      await resetV2Deployment(code);
      setV2Info(null);
      await refreshSchool();
    } catch (err) {
      console.error('Error resetting V2:', err);
      alert('Erreur lors de la réinitialisation: ' + (err as Error).message);
    } finally {
      setV2Resetting(false);
    }
  };

  const handleV2Wipe = async () => {
    if (!code) return;
    if (
      !confirm(
        'Vider le contenu du tenant V2 ? Toutes les données seront effacées puis l\'environnement sera réinitialisé comme au premier déploiement (le tenant et ses accès sont conservés).'
      )
    ) {
      return;
    }

    try {
      setV2Wiping(true);
      await wipeV2Content(code);
      setV2Info(null);
      setV2ProgressRequested(true);
      await refreshSchool();
    } catch (err) {
      console.error('Error wiping V2 content:', err);
      alert('Erreur lors de la réinitialisation du contenu: ' + (err as Error).message);
    } finally {
      setV2Wiping(false);
    }
  };

  const handleV2Suspend = async () => {
    if (!code) return;

    const currentStatus = v2Info?.tenant?.status || 'ACTIVE';
    const newStatus = currentStatus === 'SUSPENDED' ? 'ACTIVE' : 'SUSPENDED';
    const action = newStatus === 'SUSPENDED' ? 'suspendre' : 'réactiver';

    if (!confirm(`Êtes-vous sûr de vouloir ${action} cette école en V2 ?`)) {
      return;
    }

    try {
      setV2Suspending(true);
      await changeV2Status(code, { status: newStatus });
      await fetchV2Info();
    } catch (err) {
      console.error('Error changing V2 status:', err);
      alert(`Erreur lors de l'action: ` + (err as Error).message);
    } finally {
      setV2Suspending(false);
    }
  };

  const openMigrateDialog = async () => {
    try {
      const result = await getV2TenantGroups();
      setV2TenantGroups(result.data.filter((g) => g.id !== v2Info?.tenantGroupId));
      setV2SelectedGroupId('');
      setV2MigrateDialogOpen(true);
    } catch (err) {
      console.error('Error loading tenant groups:', err);
      alert('Erreur lors du chargement des réseaux: ' + (err as Error).message);
    }
  };

  const handleV2Migrate = async () => {
    if (!code || !v2SelectedGroupId) return;

    try {
      setV2MigrateLoading(true);
      await migrateV2(code, { targetTenantGroupId: v2SelectedGroupId });
      setV2MigrateDialogOpen(false);
      await fetchV2Info();
      await refreshSchool();
    } catch (err) {
      console.error('Error migrating V2:', err);
      alert('Erreur lors de la migration: ' + (err as Error).message);
    } finally {
      setV2MigrateLoading(false);
    }
  };

  const handleCreateBackup = async (version: Env) => {
    try {
      await createSchoolBackup(code!, version);
      await fetchBackups();
    } catch (err) {
      console.error('Error creating backup:', err);
    }
  };

  const handleDownloadBackup = (backup: Backup) => {
    const url = getBackupDownloadUrl(code!, backup.id);
    startDownload(url, backup.fileName);
  };

  const handleDeleteBackup = async (backupId: number) => {
    if (!confirm('Etes-vous sur de vouloir supprimer cette sauvegarde ?')) return;
    try {
      await deleteSchoolBackup(code!, backupId);
      await fetchBackups();
    } catch (err) {
      console.error('Error deleting backup:', err);
    }
  };

  const getStatusLabel = (status: string | null) => {
    const found = options?.statuses.find((s) => s.value === status);
    return found?.label || status || '-';
  };

  // Statut V2 : celui du tenant si on l'a rafraîchi depuis V2, sinon le statut
  // de déploiement connu en base ADM (aucun appel V2 à l'ouverture de la page).
  const getV2StatusLabel = () => {
    if (!school?.hasV2) return 'Non déployé';
    switch (v2Info?.tenant?.status) {
      case 'ACTIVE':
        return 'Actif';
      case 'SUSPENDED':
        return 'Suspendu';
      case 'INACTIVE':
        return 'Inactif';
    }
    switch (school.v2SetupStatus) {
      case 'in_progress':
      case 'initializing':
      case 'not_started':
        return 'En cours';
      case 'failed':
        return 'Échec';
      default:
        return 'Déployé';
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10 rounded-lg" />
          <Skeleton className="h-8 w-64" />
        </div>
        <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
          {[...Array(7)].map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <Skeleton className="h-[400px]" />
      </div>
    );
  }

  if (!school || !options) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">École non trouvée</p>
        <Button asChild className="mt-4">
          <Link to="/schools">Retour aux écoles</Link>
        </Button>
      </div>
    );
  }

  const envBackups = backups.filter(
    (backup) => (backup.version === 'v2' ? 'v2' : 'v1') === env
  );

  // Historique restreint aux points de mesure V2 (années sans V2 masquées).
  const v2History = metricsHistory
    .map((sy) => ({ ...sy, data: sy.data.filter((dp) => dp.v2) }))
    .filter((sy) => sy.data.length > 0);

  // Les couleurs des onglets d'environnement sont dans themes.css (.env-tab-*) :
  // les styles [role="tab"] y sont en !important et écrasent les utilitaires.
  const envTabTriggerClass = cn(
    'rounded-md px-4 py-2 env-tab',
    env === 'v1' ? 'env-tab-v1' : 'env-tab-v2'
  );

  // Voyants du switch : vert = opérationnel, rouge = problème, null = non déployé.
  const v1Light: EnvLight = !school.hasV1
    ? null
    : school.status === 'cancelled' || school.maintenance || school.overdueAccount
      ? 'red'
      : 'green';
  const v2Light: EnvLight = !school.hasV2
    ? null
    : v2Info?.tenant?.status === 'SUSPENDED' ||
        v2Info?.tenant?.status === 'INACTIVE' ||
        school.v2SetupStatus === 'failed'
      ? 'red'
      : 'green';

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="text-sm text-muted-foreground">
        Accueil /{' '}
        <Link to="/schools" className="hover:underline">
          Écoles
        </Link>{' '}
        / <span className="text-foreground font-medium">{school.name}</span>
      </div>

      {/* Header : identité de l'école (commun) + actions principales */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">
            {school.name}
          </h1>
          {school.vip && (
            <span className="inline-flex items-center rounded-md bg-amber-100 px-2 py-0.5 text-xs font-bold text-amber-700 border border-amber-300">
              VIP
            </span>
          )}
          {school.id && (
            <button
              onClick={() => toggleFavorite(school.code)}
              className="p-1 rounded hover:bg-muted transition-colors"
              title={isFavorite(school.id) ? 'Retirer des favoris' : 'Ajouter aux favoris'}
            >
              <Star className={cn('h-5 w-5', isFavorite(school.id) ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300')} />
            </button>
          )}
        </div>
        {/* Actions principales */}
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" asChild>
            <Link to="/schools">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Retour à la liste
            </Link>
          </Button>
          <Button variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50">
            <Trash2 className="mr-2 h-4 w-4" />
            Supprimer
          </Button>
          {env === 'v1' && school.hasV1 && (
            <Button variant="default" className="bg-blue-500 hover:bg-blue-600" asChild>
              <a
                href={school.whiteLabel ? `https://${school.whiteLabel}` : `https://app.ecole-futee.com/signin/index?sclid=${school.code}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLink className="mr-2 h-4 w-4" />
                Accéder V1
              </a>
            </Button>
          )}
          {env === 'v2' && school.hasV2 && (
            <Button variant="default" className="bg-green-500 hover:bg-green-600" asChild>
              <a
                href={school.v2TenantId ? `https://my.ecole-futee.com/?school-connect=${school.v2TenantId}` : 'https://my.ecole-futee.com'}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLink className="mr-2 h-4 w-4" />
                Accéder V2
              </a>
            </Button>
          )}
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">{error}</div>
      )}

      {/* Tabs : onglets communs, onglets d'environnement, switch V1/V2 à droite */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <div className="flex flex-wrap items-center justify-between gap-3 pt-4 pb-4 border-b border-border/60">
        <TabsList className="bg-muted/50 p-1 rounded-lg w-fit gap-1">
          <TabsTrigger
            value="informations"
            className="rounded-md px-4 py-2 data-[state=active]:bg-white data-[state=active]:shadow-sm"
          >
            <School className="h-4 w-4 mr-1" />
            École
          </TabsTrigger>
          <TabsTrigger
            value="etablissements"
            className="rounded-md px-4 py-2 data-[state=active]:bg-white data-[state=active]:shadow-sm"
          >
            <Building2 className="h-4 w-4 mr-1" />
            Établissements
          </TabsTrigger>
          <TabsTrigger
            value="facturation"
            className="rounded-md px-4 py-2 data-[state=active]:bg-white data-[state=active]:shadow-sm"
          >
            <Receipt className="h-4 w-4 mr-1" />
            Facturation
          </TabsTrigger>

          <div className="w-px h-6 bg-border mx-1" aria-hidden="true" />

          {env === 'v2' && school.hasV2 && (
            <TabsTrigger value="activite" className={envTabTriggerClass}>
              <Activity className="h-4 w-4 mr-1" />
              Activité
            </TabsTrigger>
          )}
          {env === 'v1' && (
            <TabsTrigger value="branding" className={envTabTriggerClass}>
              <Image className="h-4 w-4 mr-1" />
              Branding
            </TabsTrigger>
          )}
          <TabsTrigger value="environnement" className={envTabTriggerClass}>
            <Settings2 className="h-4 w-4 mr-1" />
            Configuration
          </TabsTrigger>
          <TabsTrigger value="statistiques" className={envTabTriggerClass}>
            <BarChart3 className="h-4 w-4 mr-1" />
            Statistiques
          </TabsTrigger>
          <TabsTrigger value="backups" className={envTabTriggerClass}>
            <HardDrive className="h-4 w-4 mr-1" />
            Sauvegardes
          </TabsTrigger>
        </TabsList>

        <EnvSwitch
          env={env}
          onChange={handleEnvChange}
          v1Status={
            !school.hasV1
              ? 'Non déployé'
              : [
                  getStatusLabel(school.status),
                  school.maintenance && 'maintenance',
                  school.overdueAccount && 'impayé',
                ]
                  .filter(Boolean)
                  .join(' · ')
          }
          v2Status={getV2StatusLabel()}
          v1Light={v1Light}
          v2Light={v2Light}
        />
        </div>

        {/* École Tab — espace commun ; indicateurs de l'environnement sélectionné */}
        <TabsContent value="informations" className="mt-6 space-y-6">
          {env === 'v1' ? (
            school.hasV1 ? (
              <div className="grid gap-3 grid-cols-2 md:grid-cols-3 xl:grid-cols-6">
                <StatCard title="Élèves" value={metrics?.studentCount || 0} icon={GraduationCap} color="teal" />
                <StatCard title="Parents" value={metrics?.parentCount || 0} icon={Users} color="cyan" />
                <StatCard title="Enseignants" value={metrics?.teacherCount || 0} icon={Briefcase} color="cyan" />
                <StatCard title="Classes" value={metrics?.classCount || 0} icon={Star} color="blue" />
                <StatCard title="Connexions" value={metrics?.connexionCount || 0} icon={LogIn} color="pink" />
                <StatCard title="Mails envoyés" value={metrics?.mailCount || 0} icon={Mail} color="pink" />
              </div>
            ) : (
              <div className="rounded-xl border border-dashed p-4 text-sm text-muted-foreground">
                Cette école n'est pas déployée en V1 — aucune métrique disponible.
              </div>
            )
          ) : metrics?.v2 ? (
            <div className="grid gap-3 grid-cols-2 md:grid-cols-3 xl:grid-cols-7">
              <StatCard title="Élèves" value={metrics.v2.studentCount} icon={GraduationCap} color="green" />
              <StatCard title="Parents" value={metrics.v2.parentCount} icon={Users} color="green" />
              <StatCard title="Enseignants" value={metrics.v2.teacherCount} icon={Briefcase} color="green" />
              <StatCard title="Classes" value={metrics.v2.classCount} icon={Star} color="green" />
              <StatCard title="Niveaux" value={metrics.v2.levelCount} icon={Layers} color="green" />
              <StatCard title="Matières" value={metrics.v2.subjectCount} icon={BookOpen} color="green" />
              <StatCard title="Mails envoyés" value={metrics.v2.mailCount} icon={Mail} color="green" />
            </div>
          ) : (
            <div className="rounded-xl border border-dashed p-4 text-sm text-muted-foreground">
              {school.hasV2
                ? 'Aucune métrique V2 collectée pour le moment.'
                : "Cette école n'est pas déployée en V2 — aucune métrique disponible."}
            </div>
          )}

          <div className="grid gap-6 md:grid-cols-2 items-start">
            <div className="space-y-6">
              <GeneralInfoCard school={school} options={options} onSaved={refreshSchool} />
              {/* Historique des imports de données (assistant d'import V2) */}
              {env === 'v2' && school.hasV2 && hasPermission('action.v2.import') && (
                <DataImportsCard
                  schoolCode={school.code}
                  importV2Url={`/schools/${school.code}/import-v2`}
                />
              )}
            </div>
            <div className="space-y-6">
              {/* Offre de l'environnement sélectionné */}
              {env === 'v1' && school.hasV1 && (
                <V1DeploymentCard school={school} options={options} onSaved={refreshSchool} />
              )}
              {env === 'v2' && (school.hasV2 || school.v2Subscription) && (
                <V2SubscriptionCard school={school} options={options} onSaved={refreshSchool} />
              )}
              <ContactsCard school={school} options={options} onSaved={refreshSchool} />
              {/* Réseaux (physique/virtuels) + URL marque blanche + aperçu de la
                  page de connexion. Le branding V2 se gère depuis la page du réseau. */}
              <NetworkCard school={school} onSaved={refreshSchool} />
              {/* Les directeurs n'existent qu'en V2 : visibles uniquement sur cet environnement */}
              {env === 'v2' && school.hasV2 && <V2DirectorsCard schoolCode={school.code} />}
            </div>
          </div>
        </TabsContent>

        {/* Établissements Tab — espace commun (le contenu charge ses données à l'affichage) */}
        <TabsContent value="etablissements" className="mt-6">
          {activeTab === 'etablissements' && (
            <EstablishmentsCard
              schoolCode={school.code}
              isEducationNationale={school.isEducationNationale || false}
              hasV1={school.hasV1 || false}
              hasV2={school.hasV2 || false}
            />
          )}
        </TabsContent>

        {/* Branding Tab — V1 uniquement : images de l'école synchronisées vers
            la V1. En V2 le branding se gère au niveau du réseau physique. */}
        {env === 'v1' && (
          <TabsContent value="branding" className="mt-6">
            <div className="grid gap-6 md:grid-cols-2">
              <BrandingImagesCard school={school} onSaved={refreshSchool} />
            </div>
          </TabsContent>
        )}

        {/* Facturation Tab — espace commun */}
        <TabsContent value="facturation" className="mt-6">
          <Card>
            <CardHeader icon={<Receipt />} iconColor="orange">
              <CardTitle>Facturation</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                <Construction className="h-12 w-12 mb-4 opacity-50" />
                <p className="text-lg font-medium">En cours de construction</p>
                <p className="text-sm mt-1">Cette fonctionnalite sera disponible prochainement.</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Configuration Tab — contenu V1 ou V2 selon le switch */}
        <TabsContent value="environnement" className="mt-6">
          {env === 'v1' ? (
            !school.hasV1 ? (
              <Card>
                <CardHeader icon={<Database />} iconColor="blue">
                  <CardTitle>Environnement V1</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                    <Rocket className="h-12 w-12 mb-4 opacity-40" />
                    <p className="text-lg font-medium">Aucun déploiement V1</p>
                    <p className="text-sm mt-1 mb-4">
                      Cette école n'est pas déployée sur l'environnement V1.
                    </p>
                    <Button onClick={() => setV1DeployDialogOpen(true)}>
                      <Rocket className="mr-2 h-4 w-4" />
                      Déployer en V1
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                <div className="grid gap-6 md:grid-cols-2 items-start">
                  <V1DatabaseCard school={school} onSaved={refreshSchool} />
                  <Card>
                    <CardHeader icon={<Server />} iconColor="blue">
                      <CardTitle>Progression du déploiement V1</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <V1DeploymentStatus
                        schoolCode={school.code}
                        autoRefresh={activeTab === 'environnement' && env === 'v1'}
                        refreshInterval={2000}
                      />
                    </CardContent>
                  </Card>
                </div>
              </div>
            )
          ) : (
            <div className="space-y-6">
              {!school.hasV2 ? (
                <Card>
                  <CardHeader icon={<Database />} iconColor="green">
                    <CardTitle>Environnement V2</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                      <Rocket className="h-12 w-12 mb-4 opacity-40" />
                      <p className="text-lg font-medium">Aucun déploiement V2</p>
                      <p className="text-sm mt-1 mb-4">
                        Cette école n'est pas déployée sur l'environnement V2.
                      </p>
                      {hasPermission('action.v2.deploy') && (
                        <Button asChild>
                          <Link to={`/schools/${school.code}/upgrade-v2`}>
                            <Rocket className="mr-2 h-4 w-4" />
                            Déployer en V2
                          </Link>
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-6 md:grid-cols-2">
                  {/* V2 Info Card — données en base ; « Actualiser » interroge la V2 */}
                  <Card>
                    <CardHeader
                      icon={<Database />}
                      iconColor="green"
                      actions={
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={fetchV2Info}
                          disabled={v2Loading}
                          title="Récupérer le statut du tenant depuis la V2"
                        >
                          <RefreshCw className={cn('h-4 w-4 mr-1', v2Loading && 'animate-spin')} />
                          Actualiser
                        </Button>
                      }
                    >
                      <CardTitle>Informations V2</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">Version</p>
                          <Badge variant="outline">V2</Badge>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">Reseau</p>
                          <p className="text-sm">
                            {options.networks.find((n) => n.id === school.networkId)?.name ||
                              'Ecole Futee (defaut)'}
                          </p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">Tenant ID</p>
                          <p className="text-xs font-mono text-muted-foreground truncate">
                            {school.v2TenantId || '—'}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">Statut</p>
                          <Badge
                            variant={
                              v2Info?.tenant?.status === 'ACTIVE'
                                ? 'success'
                                : v2Info?.tenant?.status === 'SUSPENDED'
                                  ? 'destructive'
                                  : v2Info?.tenant?.status === 'INACTIVE'
                                    ? 'warning'
                                    : 'default'
                            }
                          >
                            {getV2StatusLabel()}
                          </Badge>
                        </div>
                      </div>
                      <div className="pt-2 border-t">
                        <p className="text-sm font-medium text-muted-foreground mb-2">Actions</p>
                        <div className="flex flex-wrap gap-2">
                          {hasPermission('action.v2.migrate') && (
                            <Button variant="outline" size="sm" onClick={openMigrateDialog}>
                              <RefreshCw className="h-4 w-4 mr-1" />
                              Migrer vers un autre reseau
                            </Button>
                          )}
                          {hasPermission('action.v2.status') && (
                            <Button
                              variant="outline"
                              size="sm"
                              className={
                                v2Info?.tenant?.status === 'SUSPENDED'
                                  ? 'text-green-600'
                                  : 'text-yellow-600'
                              }
                              onClick={handleV2Suspend}
                              disabled={v2Suspending}
                            >
                              {v2Suspending ? (
                                <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                              ) : v2Info?.tenant?.status === 'SUSPENDED' ? (
                                'Reactiver'
                              ) : (
                                'Suspendre'
                              )}
                            </Button>
                          )}
                          {hasPermission('action.v2.reset') && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                              onClick={handleV2Wipe}
                              disabled={v2Wiping}
                              title="Efface toutes les données du tenant et rejoue l'initialisation (tenant conservé)"
                            >
                              {v2Wiping ? (
                                <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                              ) : (
                                <Eraser className="h-4 w-4 mr-1" />
                              )}
                              Vider le contenu
                            </Button>
                          )}
                          {hasPermission('action.v2.reset') && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={handleV2Reset}
                              disabled={v2Resetting}
                              title="Supprime le tenant V2 (pour redéployer ensuite)"
                            >
                              {v2Resetting ? (
                                <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                              ) : (
                                <Trash2 className="h-4 w-4 mr-1" />
                              )}
                              Réinitialiser
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* V2 Status Card — la progression n'interroge la V2 que si un
                      déploiement est en cours, ou à la demande */}
                  <Card>
                    <CardHeader icon={<Server />} iconColor="teal">
                      <CardTitle>Progression du déploiement</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {school.v2SetupStatus !== 'completed' || v2ProgressRequested ? (
                        <V2DeploymentStatus
                          schoolCode={school.code}
                          autoRefresh={activeTab === 'environnement' && env === 'v2'}
                          refreshInterval={1000}
                        />
                      ) : (
                        <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                          <CheckCircle2 className="h-10 w-10 mb-3 text-green-500" />
                          <p className="text-sm font-medium text-foreground">Déploiement terminé</p>
                          <Button
                            variant="outline"
                            size="sm"
                            className="mt-4"
                            onClick={() => setV2ProgressRequested(true)}
                          >
                            <RefreshCw className="h-4 w-4 mr-1" />
                            Afficher le détail des étapes
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}

            </div>
          )}
        </TabsContent>

        {/* Statistiques Tab — historique de l'environnement sélectionné */}
        <TabsContent value="statistiques" className="mt-6 space-y-6">
          {metricsHistoryLoading ? (
            <Card>
              <CardContent className="py-8">
                <div className="flex items-center justify-center gap-2 text-muted-foreground">
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  Chargement de l'historique...
                </div>
              </CardContent>
            </Card>
          ) : env === 'v1' ? (
            metricsHistory.length === 0 ? (
              <Card>
                <CardContent className="py-8">
                  <p className="text-center text-muted-foreground">Aucun historique disponible.</p>
                </CardContent>
              </Card>
            ) : (
              metricsHistory.map((sy) => (
                <Card key={sy.schoolYear}>
                  <CardHeader icon={<BarChart3 />} iconColor="blue">
                    <CardTitle>Année fiscale : {sy.schoolYear}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="text-right">Date</TableHead>
                            <TableHead className="text-center">Classes</TableHead>
                            <TableHead className="text-center">Enseignants</TableHead>
                            <TableHead className="text-center">Élèves</TableHead>
                            <TableHead className="text-center">Mails envoyés</TableHead>
                            <TableHead className="text-center">Connexions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {sy.data.map((dp) => (
                            <TableRow key={dp.date}>
                              <TableCell className="text-right font-medium">
                                {formatHistoryDate(dp.date)}
                              </TableCell>
                              <TableCell className="text-center">{dp.classCount ?? '—'}</TableCell>
                              <TableCell className="text-center">{dp.teacherCount ?? '—'}</TableCell>
                              <TableCell className="text-center">{dp.studentCount ?? '—'}</TableCell>
                              <TableCell className="text-center">{dp.mailCount ?? '—'}</TableCell>
                              <TableCell className="text-center">{dp.connexionCount ?? '—'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              ))
            )
          ) : v2History.length === 0 ? (
            <Card>
              <CardContent className="py-8">
                <p className="text-center text-muted-foreground">Aucun historique V2 disponible.</p>
              </CardContent>
            </Card>
          ) : (
            v2History.map((sy) => (
              <Card key={sy.schoolYear}>
                <CardHeader icon={<BarChart3 />} iconColor="green">
                  <CardTitle>Année fiscale : {sy.schoolYear}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-right">Date</TableHead>
                          <TableHead className="text-center">Classes</TableHead>
                          <TableHead className="text-center">Enseignants</TableHead>
                          <TableHead className="text-center">Élèves</TableHead>
                          <TableHead className="text-center">Mails envoyés</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sy.data.map((dp) => (
                          <TableRow key={dp.date}>
                            <TableCell className="text-right font-medium">
                              {formatHistoryDate(dp.date)}
                            </TableCell>
                            <TableCell className="text-center">{dp.v2?.classCount ?? '—'}</TableCell>
                            <TableCell className="text-center">{dp.v2?.teacherCount ?? '—'}</TableCell>
                            <TableCell className="text-center">{dp.v2?.studentCount ?? '—'}</TableCell>
                            <TableCell className="text-center">{dp.v2?.mailCount ?? '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        {/* Sauvegardes Tab — filtrées par environnement */}
        <TabsContent value="backups" className="mt-6">
          <Card>
            <CardHeader
              icon={<Save />}
              iconColor="teal"
              actions={
                <Button
                  size="sm"
                  onClick={() => handleCreateBackup(env)}
                  disabled={env === 'v2' ? !school.hasV2 : !school.hasV1}
                  className={
                    env === 'v1'
                      ? 'bg-blue-500 hover:bg-blue-600'
                      : 'bg-green-500 hover:bg-green-600'
                  }
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Créer une sauvegarde {env === 'v1' ? 'V1' : 'V2'}
                </Button>
              }
            >
              <CardTitle>Sauvegardes {env === 'v1' ? 'V1' : 'V2'}</CardTitle>
            </CardHeader>
            <CardContent>
              {backupsLoading ? (
                <Skeleton className="h-32" />
              ) : envBackups.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  Aucune sauvegarde {env === 'v1' ? 'V1' : 'V2'}
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead></TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Fichier</TableHead>
                      <TableHead>Poids</TableHead>
                      <TableHead className="text-right w-[1%] whitespace-nowrap">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {envBackups.map((backup) => (
                      <TableRow key={backup.id}>
                        <TableCell>
                          {backup.type === 'auto' ? (
                            <Clock className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <Save className="h-4 w-4 text-muted-foreground" />
                          )}
                        </TableCell>
                        <TableCell>{formatDateTime(backup.createdAt)}</TableCell>
                        <TableCell className="font-mono text-sm">{backup.fileName}</TableCell>
                        <TableCell>{backup.sizeFormatted}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              onClick={() => handleDownloadBackup(backup)}
                              className="text-blue-500 hover:text-blue-600 hover:bg-blue-50"
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              onClick={() => handleDeleteBackup(backup.id)}
                              className="text-red-500 hover:text-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Activité Tab — journal d'audit du tenant V2 (monté à l'ouverture) */}
        {env === 'v2' && school.hasV2 && (
          <TabsContent value="activite" className="mt-6">
            <V2ActivityTab schoolCode={school.code} />
          </TabsContent>
        )}
      </Tabs>

      {/* Deploy Dialogs */}
      <V1DeployDialog
        open={v1DeployDialogOpen}
        onOpenChange={setV1DeployDialogOpen}
        schoolCode={school.code}
        schoolName={school.name}
        onSuccess={() => {
          refreshSchool();
        }}
      />

      {/* V2 Migrate Dialog */}
      <Dialog open={v2MigrateDialogOpen} onOpenChange={setV2MigrateDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Migrer vers un autre réseau</DialogTitle>
            <DialogDescription>
              Sélectionnez le réseau V2 vers lequel migrer l'école{' '}
              <strong>{school.name}</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="targetGroup">Réseau cible</Label>
              <select
                id="targetGroup"
                value={v2SelectedGroupId}
                onChange={(e) => setV2SelectedGroupId(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">Sélectionnez un réseau...</option>
                {v2TenantGroups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name} ({group.tenantCount} écoles)
                  </option>
                ))}
              </select>
            </div>
            {v2SelectedGroupId && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-800">
                  Attention : La migration va transférer l'école vers le réseau sélectionné. Cette
                  opération peut prendre quelques instants.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setV2MigrateDialogOpen(false)}
              disabled={v2MigrateLoading}
            >
              Annuler
            </Button>
            <Button
              onClick={handleV2Migrate}
              disabled={!v2SelectedGroupId || v2MigrateLoading}
            >
              {v2MigrateLoading ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Migration...
                </>
              ) : (
                'Migrer'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DownloadProgressModal
        open={modalOpen}
        onOpenChange={(open) => { if (!open) resetDownload(); else setModalOpen(open); }}
        state={downloadInfo.state}
        progress={downloadInfo.progress}
        fileName={downloadInfo.fileName}
        fileSize={downloadInfo.fileSize}
        error={downloadInfo.error}
      />
    </div>
  );
}
